import { Col, Row } from "react-bootstrap"
import { baseURL } from "../../api"
import { useState } from "react"
import { GameLogicBlock } from "../../types/LogicTypes"
import { renderCmd, generateBlockIdsRecursive, getLogicWithoutIds, getBlockById } from "./helpers"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBolt, faDice, faGears, faScaleBalanced } from "@fortawesome/free-solid-svg-icons"
import EditBlockModal from "./EditModal"
import { statName } from "../../helpers/gameHelpers"

interface Props {
}
const StoryEditor: React.FC<Props> = () => {

    const [ logic, setLogic ] = useState<GameLogicBlock>({})
    const [ selectedBlockId, setSelectedBlockId ] = useState("")

    const loadFile = async (filename: string) => {
        const res = await fetch(`/templates/${filename}`)
        const logic = await res.json()
        generateBlockIdsRecursive([logic])
        setLogic(logic)
    }

    const selectBlock = (id: string | undefined) => {
        setSelectedBlockId(id ?? "")
        console.log(getBlockById([logic], id ?? ""))
        return false
    }

    const apply = (block: Partial<GameLogicBlock>) => {
        // console.log("TODO: Apply block", block)
        const b = getBlockById([logic], selectedBlockId ?? "")
        if (b) {
            // All modifications here
            b.openDialogue = block.openDialogue !== "" ? block.openDialogue : undefined
            b.message = block.message !== "" ? block.message : undefined
            b.run = block.run !== "" ? block.run : undefined
            b.type = block.type !== "" ? block.type : undefined
            b.testAction = (block.testAction && b.testAction) ? {
                ...b.testAction,
                stat: block.testAction.stat,
                statMod: block.testAction.statMod,
                req: block.testAction.req,
                pass: b.testAction.pass,
                fail: b.testAction.fail,
            } : undefined

            setLogic(logic)
            setSelectedBlockId("")
            return
        }
        console.error("No block found")
        alert("No block found")
    }

    return (
        <>
            <div className="mainbg" style={{backgroundImage: `url(${baseURL}/img/graphics/main_bg_01.png)`}}>
                <Row className="mt-5">
                    <Col lg="2">
                        <h4>Templates</h4>
                        <div className="clickable" onClick={() => { loadFile("basic_encounter.json") }}>Basic Encounter</div>
                        <div className="clickable" onClick={() => { loadFile("complex_encounter_1.json") }}>Complex Encounter</div>
                        <div className="clickable" onClick={() => { loadFile("complex_encounter_clue_1.json") }}>Complex Encounter (Clue 1)</div>
                        <h5 className="mt-3 mb-0">Condition</h5>
                        <div className="clickable" onClick={() => { loadFile("condition_basic.json") }}>Condition</div>
                        <div className="clickable" onClick={() => { loadFile("condition_with_test.json") }}>Condition (with test)</div>
                        <div className="clickable" onClick={() => { loadFile("condition_silent.json") }}>Condition (with silent test)</div>
                        <h5 className="mt-3 mb-0">Mythos</h5>
                        <div className="clickable" onClick={() => { loadFile("mythos_green.json") }}>Mythos (Green)</div>
                        <div className="clickable" onClick={() => { loadFile("mythos_green_clue.json") }}>Mythos (Green; Spend Clue)</div>
                        <div className="clickable" onClick={() => { loadFile("mythos_yellow.json") }}>Mythos (Yellow)</div>
                        <div className="clickable" onClick={() => { loadFile("mythos_yellow_clue.json") }}>Mythos (Yellow; Spend Clue)</div>
                    </Col>
                    <Col lg="6">
                        <h4>Blocks</h4>
                        <LogicBlock key={logic.id} block={logic} onClick={selectBlock} s={selectedBlockId} />
                    </Col>
                    <Col lg="4">
                        <h4>Code <span className="clickable ms-2" style={{fontSize: "9pt"}} onClick={() => { navigator.clipboard.writeText(JSON.stringify(getLogicWithoutIds([structuredClone(logic)])[0], null, 4)) }}>Copy</span></h4>
                        <div style={{whiteSpace: "pre", fontFamily: "monospace", fontSize: "9pt"}}>
                            {JSON.stringify(getLogicWithoutIds([structuredClone(logic)])[0], null, 4)}
                        </div>
                    </Col>

                </Row>
            </div>

            <EditBlockModal
                block={structuredClone(getBlockById([logic], selectedBlockId))}
                selectedBlockId={selectedBlockId}
                setSelectedBlockId={setSelectedBlockId}
                apply={apply}
            />
        </>
    )
}

interface logicBlockProps {
    block: GameLogicBlock
    className?: string
    onClick: (id: string | undefined) => void
    s: string  // Selected block id
    isAction?: boolean
}
const LogicBlock: React.FC<logicBlockProps> = (props) => {
    return (
        <>
            <div className={`renderedLogicBlock mb-1${props.s === props.block.id ? " selected" : ""}`} onClick={(e) => { e.stopPropagation(); props.onClick(props.block.id)}}>
                {props.block.openDialogue && <div>Dialogue: {props.block.openDialogue}</div> }
                {props.block.type && <div>Type: {props.block.type}</div> }
                {props.block.condition && <div><FontAwesomeIcon icon={faScaleBalanced} /> Condition: <span className="cmd">{props.block.condition}</span></div> }

                {(props.block.message && !props.isAction) && <div className="mt-2" style={{fontStyle: "italic"}}>"{props.block.message}"</div> }

                {props.block.run && <div><FontAwesomeIcon icon={faGears} /> Run: {props.block.run.replaceAll(">", "").split(",").map(cmd => <span className="cmd">{renderCmd(cmd)}</span>)}</div> }
                {props.block.worldRun && <div><FontAwesomeIcon icon={faGears} /> WorldRun: {props.block.worldRun.replaceAll(">", "").split(",").map(cmd => <span className="cmd">{renderCmd(cmd)}</span>)}</div> }

                {
                    props.block.action &&
                    <>
                        <div className="text-center mt-2"><FontAwesomeIcon icon={faBolt} /> Action: <i>"{props.block.action.message}"</i></div>
                        <LogicBlock block={props.block.action} onClick={props.onClick} isAction s={props.s} />
                    </>
                }

                {
                    props.block.testAction &&
                    <>
                        <div className="mt-2 text-center"> <FontAwesomeIcon icon={faDice} /> <b>Test</b> {statName(props.block.testAction.stat)} {props.block.testAction.statMod && `+${props.block.testAction.statMod}`}</div>
                        <Row>
                            <Col>
                                <div className="text-center">Pass</div>
                            </Col>
                            <Col>
                                <div className="text-center">Fail</div>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                {props.block.testAction.pass.map(l => <LogicBlock key={l.id} block={l} onClick={props.onClick} s={props.s} />)}
                            </Col>
                            <Col>
                                {props.block.testAction.fail.map(l => <LogicBlock key={l.id} block={l} onClick={props.onClick} s={props.s} />)}
                            </Col>
                        </Row>
                    </>
                }

            { (props.block.logic ?? []).map(l => <LogicBlock key={l.id} block={l} onClick={props.onClick} s={props.s} />) }
            </div>
        </>
    )
}



export default StoryEditor